import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Card from '../components/card/Card';
import P from '../components/texts/p/P';
import Image from '../components/image/Image';
import Table from '../components/table/table';
import Button from '../components/button/Button';
import Icon from '../components/icon/Icon';
import Input from '../components/input/Input';
import DatePicker from '../components/datepicker/DatePicker';
import CheckBox from '../components/checkBox/CheckBox';
import Modal from '../components/modal/modal';
import TableSkeleton from '../components/TableSkeleton/TableSkeleton';
import { routes } from '../routes/routePaths';
import { ITableData } from '../types/interfaces';
import { getMonetizedAvalesColumns } from '../utils/monetizedAvalesColumns';
import { resolveMonetizedAvalesSearch } from '../utils/monetizedAvalesSearch';
import { useFiscalIdConfig } from '../hooks/useFiscalIdConfig';
import {
  getMonetizedAvales,
  exportMonetizedAvalesCsv,
  MonetizedAvalData,
  MonetizedAvalesFilterParams,
} from '../services/GuaranteesService';

const ITEMS_PER_PAGE = 30;

const GUARANTEE_TYPES = [
  { label: 'Cheques', value: 'CHEQUES' },
  { label: 'Prestamos', value: 'PRESTAMOS' },
  { label: 'Pagares', value: 'PAGARES' },
  { label: 'Aval comercial', value: 'AVAL COMERCIAL' },
  { label: 'Operaciones públicas', value: 'OPERACIONES PUBLICAS' },
  { label: 'Obligaciones noegociables', value: 'OBLIGACIONES NEGOCIABLES' },
];

const ALL_TYPE_VALUES = GUARANTEE_TYPES.map(t => t.value);

const MonetizedAvalesTab = () => {
  const fiscalId = useFiscalIdConfig();
  const navigate = useNavigate();

  const [data, setData] = useState<MonetizedAvalData[]>([]);
  const [tableData, setTableData] = useState<ITableData>({
    page: 0,
    size: ITEMS_PER_PAGE,
    totalElements: 0,
    totalPages: 0,
  });
  const [, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [globalId, setGlobalId] = useState('');
  const [operationNumber, setOperationNumber] = useState('');
  const [monetizationDateFrom, setMonetizationDateFrom] = useState('');
  const [monetizationDateTo, setMonetizationDateTo] = useState('');
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  const [dateError, setDateError] = useState('');

  // Temporary filter states for modal
  const [tempSelectedTypes, setTempSelectedTypes] = useState<string[]>([]);
  const [tempAllSelected, setTempAllSelected] = useState(true); // "Todos" selected by default
  const [tempGlobalId, setTempGlobalId] = useState('');
  const [tempOperationNumber, setTempOperationNumber] = useState('');
  const [tempMonetizationDateFrom, setTempMonetizationDateFrom] = useState('');
  const [tempMonetizationDateTo, setTempMonetizationDateTo] = useState('');
  const [tempDueDateFrom, setTempDueDateFrom] = useState('');
  const [tempDueDateTo, setTempDueDateTo] = useState('');

  // Dropdown state
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Track if "Todos" is selected (no filter applied)
  const [allSelected, setAllSelected] = useState(true);

  const [appliedFilters, setAppliedFilters] = useState<MonetizedAvalesFilterParams>({});

  const lastRequest = useRef(0);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildFilterParams = useCallback((overrideSearchText?: string): MonetizedAvalesFilterParams => {
    const text = overrideSearchText ?? searchText;
    const searchParsed = resolveMonetizedAvalesSearch(text);

    return {
      guaranteeType: !allSelected && selectedTypes.length > 0
        ? selectedTypes : undefined,
      globalId: globalId || undefined,
      searchText: searchParsed.searchText,
      operationNumber: operationNumber || undefined,
      monetizationDateFrom: monetizationDateFrom || undefined,
      monetizationDateTo: monetizationDateTo || undefined,
      dueDateFrom: dueDateFrom || undefined,
      dueDateTo: dueDateTo || undefined,
    };
  }, [allSelected, selectedTypes, searchText, globalId, operationNumber, monetizationDateFrom, monetizationDateTo, dueDateFrom, dueDateTo]);

  const fetchData = useCallback(async (targetPage: number, filters: MonetizedAvalesFilterParams) => {
    setIsLoading(true);
    const requestId = Date.now();
    lastRequest.current = requestId;

    try {
      const result = await getMonetizedAvales({
        ...filters,
        page: targetPage,
        size: ITEMS_PER_PAGE,
      });
      if (lastRequest.current !== requestId) return;

      if (result) {
        setTableData({
          page: targetPage - 1,
          size: result.size,
          totalElements: result.total_elements,
          totalPages: result.total_pages,
        });
        setData(result.content || []);
      } else {
        setData([]);
        setTableData({ page: 0, size: ITEMS_PER_PAGE, totalElements: 0, totalPages: 0 });
      }
    } catch {
      setData([]);
      setTableData({ page: 0, size: ITEMS_PER_PAGE, totalElements: 0, totalPages: 0 });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchData(1, {});
    setHasSearched(true);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchText(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      const filters = buildFilterParams(value);
      setAppliedFilters(filters);
      fetchData(1, filters);
    }, 500);
  };

  const openFilterModal = () => {
    setTempAllSelected(allSelected);
    setTempSelectedTypes([...selectedTypes]);
    setTempGlobalId(globalId);
    setTempOperationNumber(operationNumber);
    setTempMonetizationDateFrom(monetizationDateFrom);
    setTempMonetizationDateTo(monetizationDateTo);
    setTempDueDateFrom(dueDateFrom);
    setTempDueDateTo(dueDateTo);
    setDateError('');
    setIsTypeDropdownOpen(false);
    setIsFilterModalOpen(true);
  };

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
    setIsTypeDropdownOpen(false);
    setDateError('');
  };

  const handleToggleAll = () => {
    if (!tempAllSelected) {
      // Seleccionar "Todos" = limpiar selecciones individuales
      setTempAllSelected(true);
      setTempSelectedTypes([]);
    }
  };

  const handleToggleType = (value: string) => {
    // Al seleccionar un tipo individual, desmarcar "Todos"
    setTempAllSelected(false);
    setTempSelectedTypes(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleApplyFilters = () => {
    if (tempMonetizationDateFrom && tempMonetizationDateTo && tempMonetizationDateFrom > tempMonetizationDateTo) {
      setDateError('La fecha de monetización "desde" no puede ser posterior a "hasta"');
      return;
    }
    if (tempDueDateFrom && tempDueDateTo && tempDueDateFrom > tempDueDateTo) {
      setDateError('La fecha de vencimiento "desde" no puede ser posterior a "hasta"');
      return;
    }

    setAllSelected(tempAllSelected);
    setSelectedTypes(tempSelectedTypes);
    setGlobalId(tempGlobalId);
    setOperationNumber(tempOperationNumber);
    setMonetizationDateFrom(tempMonetizationDateFrom);
    setMonetizationDateTo(tempMonetizationDateTo);
    setDueDateFrom(tempDueDateFrom);
    setDueDateTo(tempDueDateTo);

    // Si "Todos" está seleccionado, no se envía filtro de tipo
    const searchParsed = resolveMonetizedAvalesSearch(searchText);
    const filters: MonetizedAvalesFilterParams = {
      guaranteeType: !tempAllSelected && tempSelectedTypes.length > 0
        ? tempSelectedTypes : undefined,
      globalId: tempGlobalId || undefined,
      searchText: searchParsed.searchText,
      operationNumber: tempOperationNumber || undefined,
      monetizationDateFrom: tempMonetizationDateFrom || undefined,
      monetizationDateTo: tempMonetizationDateTo || undefined,
      dueDateFrom: tempDueDateFrom || undefined,
      dueDateTo: tempDueDateTo || undefined,
    };

    setAppliedFilters(filters);
    setPage(1);
    fetchData(1, filters);
    closeFilterModal();
  };

  const handleClearFilters = () => {
    setAllSelected(true);
    setSelectedTypes([]);
    setSearchText('');
    setGlobalId('');
    setOperationNumber('');
    setMonetizationDateFrom('');
    setMonetizationDateTo('');
    setDueDateFrom('');
    setDueDateTo('');
    setDateError('');
    setAppliedFilters({});
    setPage(1);
    fetchData(1, {});
  };

  const handleChangePage = (newPage: number) => {
    const actualPage = newPage + 1;
    if (actualPage < 1 || actualPage > tableData.totalPages) return;
    setPage(actualPage);
    setTableData((prev) => ({ ...prev, page: newPage }));
    fetchData(actualPage, appliedFilters);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const result = await exportMonetizedAvalesCsv(appliedFilters);
      if (result && 'data' in result) {
        const blob = result.data as Blob;
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = 'avales-monetizados.csv';
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
    setIsExporting(false);
  };

  const handleEnter = (row: MonetizedAvalData) => {
    const typeMap: Record<string, string> = {
      LOAN: routes.loanDetail,
      CHECK: routes.checkDetail,
      PROMISSORY_NOTE: routes.promissoryNoteDetail,
      COMMERCIAL_ENDORSEMENT: routes.commercialEndorsementDetail,
    };
    const route = typeMap[row.instrument_type];
    if (!route || !row.line_id) return;

    const path = route
      .replace(':cuit', row.cuit)
      .replace(':lineId', String(row.line_id))
      .replace(':id', String(row.guarantee_number));
    navigate(path);
  };

  const activeFilterCount = Object.values(appliedFilters).filter(
    (v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)
  ).length;

const columns = getMonetizedAvalesColumns(handleEnter, fiscalId.country);

  return (
    <>
      {/* Toolbar: filtros + búsqueda + export */}
      <Card display='flex' gap='16px' alignItems='center' margin={{ top: '4' }}>
        <Button variant='secondary-1' onClick={openFilterModal}>
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='other_filter' variant='secondary-1' width={20} height={20} />
            <P variant='secondary-1' withMargin={false}>
              Editar filtros{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </P>
          </Card>
        </Button>
        <Card width='300px'>
          <Input
            type='text'
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            placeholder={fiscalId.guaranteeSearchPlaceholder}
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </Card>
        <Button
          variant='secondary-1'
          onClick={handleExportCsv}
          disabled={isExporting || tableData.totalElements === 0}
        >
          <Card display='flex' alignItems='center' gap='8px'>
            <Icon name='download_outline' variant='secondary-1' width={20} height={20} />
            <P variant='secondary-1' withMargin={false}>
              {isExporting ? 'Exportando...' : 'Descargar CSV'}
            </P>
          </Card>
        </Button>
        {hasSearched && tableData.totalElements > 0 && (
          <P variant='neutral' withMargin={false} type='3'>
            {tableData.totalElements.toLocaleString('es-AR')} resultado{tableData.totalElements !== 1 ? 's' : ''}
          </P>
        )}
      </Card>

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            marginTop: '8px',
            flexWrap: 'wrap',
            backgroundColor: '#F5F7FA',
            padding: '16px 24px',
            borderRadius: '4px',
            border: '1px solid #E5E7EB',
          }}
        >
          {appliedFilters.guaranteeType && appliedFilters.guaranteeType.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>Tipo:</span>{' '}
                {appliedFilters.guaranteeType.join(', ')}
              </P>
            </div>
          )}
          {(appliedFilters.monetizationDateFrom || appliedFilters.monetizationDateTo) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>Monetización:</span>{' '}
                {appliedFilters.monetizationDateFrom || '...'} — {appliedFilters.monetizationDateTo || '...'}
              </P>
            </div>
          )}
          {(appliedFilters.dueDateFrom || appliedFilters.dueDateTo) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>Vencimiento:</span>{' '}
                {appliedFilters.dueDateFrom || '...'} — {appliedFilters.dueDateTo || '...'}
              </P>
            </div>
          )}
          {appliedFilters.operationNumber && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>Operación:</span>{' '}
                {appliedFilters.operationNumber}
              </P>
            </div>
          )}
          {appliedFilters.searchText && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>{fiscalId.label} o Nº aval:</span>{' '}
                {appliedFilters.searchText}
              </P>
            </div>
          )}
          {appliedFilters.globalId && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon name='other_filter' variant='secondary-1' width={16} height={16} />
              <P variant='neutral' withMargin={false} type='3'>
                <span style={{ fontWeight: 'bold' }}>Nº aval:</span>{' '}
                {appliedFilters.globalId}
              </P>
            </div>
          )}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            onClick={handleClearFilters}
          >
            <Icon name='delete_outline' variant='neutral' width={20} height={20} />
            <P variant='neutral' withMargin={false} type='3'>Borrar filtros</P>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      <Modal
        isOpen={isFilterModalOpen}
        onClose={closeFilterModal}
        title='Filtrar avales'
        width='520px'
        primaryText='Aplicar filtros'
        primaryVariant='primary'
        onPrimaryAction={handleApplyFilters}
        secondaryText='Cancelar'
        secondaryVariant='contained'
        onSecondaryAction={closeFilterModal}
        showOverlay={true}
        overflow='visible'
      >
        <Card margin={{ top: '4' }}>
          <P type='3' variant='neutral' withMargin={true}>
            Seleccioná los filtros que quieras aplicar para ajustar los resultados según tus preferencias.
          </P>

          <Card display='grid' gap='16px' margin={{ top: '4' }}>
            {/* Tipo de garantía dropdown with checkboxes */}
            <DropdownContainer ref={dropdownRef}>
              <DropdownTrigger
                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                $isOpen={isTypeDropdownOpen}
              >
                <span style={{ color: '#888', fontSize: '14px' }}>Tipo de garantía</span>
                <ChevronIcon $isOpen={isTypeDropdownOpen}>
                  <Icon name='chevron_down_outline' variant='neutral' width={20} height={20} />
                </ChevronIcon>
              </DropdownTrigger>

{isTypeDropdownOpen && (
                <DropdownMenu>
                  <DropdownOption
                    $isSelected={tempAllSelected}
                    onClick={handleToggleAll}
                  >
                    <CheckBox
                      checked={tempAllSelected}
                      onChange={handleToggleAll}
                      size='sm'
                    >
                      <P type='2' variant='neutral' modifier='strong' withMargin={false}>
                        Todos
                      </P>
                    </CheckBox>
                  </DropdownOption>
                  {GUARANTEE_TYPES.map((type) => (
                    <DropdownOption
                      key={type.value}
                      $isSelected={tempSelectedTypes.includes(type.value)}
                      onClick={() => handleToggleType(type.value)}
                    >
                      <CheckBox
                        checked={tempSelectedTypes.includes(type.value)}
                        onChange={() => handleToggleType(type.value)}
                        size='sm'
                      >
                        <P type='2' variant='neutral' modifier='strong' withMargin={false}>
                          {type.label}
                        </P>
                      </CheckBox>
                    </DropdownOption>
                  ))}
                </DropdownMenu>
              )}
            </DropdownContainer>

            <Card display='flex' gap='16px'>
              <Card style={{ flex: 1 }}>
                <Input
                  type='text'
                  size='md'
                  placeholder='N° de aval'
                  value={tempGlobalId}
                  onChange={(e) => setTempGlobalId(e.target.value)}
                />
              </Card>
              <Card style={{ flex: 1 }}>
                <Input
                  type='text'
                  size='md'
                  placeholder='N° de operación'
                  value={tempOperationNumber}
                  onChange={(e) => setTempOperationNumber(e.target.value)}
                />
              </Card>
            </Card>

            <Card display='flex' gap='16px'>
              <Card style={{ flex: 1 }}>
                <DatePicker
                  defaultValue={tempMonetizationDateFrom}
                  onChange={(val: string) => setTempMonetizationDateFrom(val)}
                  disabled={false}
                  placeholder='Monetización desde'
                />
              </Card>
              <Card style={{ flex: 1 }}>
                <DatePicker
                  defaultValue={tempMonetizationDateTo}
                  onChange={(val: string) => setTempMonetizationDateTo(val)}
                  disabled={false}
                  placeholder='Monetización hasta'
                />
              </Card>
            </Card>

            <Card display='flex' gap='16px'>
              <Card style={{ flex: 1 }}>
                <DatePicker
                  defaultValue={tempDueDateFrom}
                  onChange={(val: string) => setTempDueDateFrom(val)}
                  disabled={false}
                  placeholder='Vencimiento desde'
                />
              </Card>
              <Card style={{ flex: 1 }}>
                <DatePicker
                  defaultValue={tempDueDateTo}
                  onChange={(val: string) => setTempDueDateTo(val)}
                  disabled={false}
                  placeholder='Vencimiento hasta'
                />
              </Card>
            </Card>

            {dateError && (
              <P variant='neutral' withMargin={false} type='3' style={{ color: '#DC2626' }}>
                {dateError}
              </P>
            )}
          </Card>
        </Card>
      </Modal>

      {/* Table */}
      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={10} columns={columns.length} />
        </Card>
      ) : data.length > 0 ? (
        <Card margin={{ top: '4' }}>
          <Table
            columns={columns}
            data={data}
            tableData={tableData}
            handleChangePage={handleChangePage}
            onRowClicked={handleEnter}
          />
        </Card>
      ) : hasSearched ? (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
          width='53vw'
        >
          <Card display='flex' justifyContent='center' margin={{ bottom: '2' }}>
            <Image name='folder_empty' />
          </Card>
          <P variant='primary' align='center' withMargin={false} weight='bold'>
            No se encontraron avales monetizados.
          </P>
          <P variant='primary' align='center'>
            Probá ajustando los filtros de búsqueda.
          </P>
        </Card>
      ) : null}
    </>
  );
};

// Styled components for the dropdown
const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownTrigger = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  padding: 12px 16px;
  border: 1px solid #585053;
  border-radius: 8px;
  background-color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const ChevronIcon = styled.div<{ $isOpen: boolean }>`
  flex-shrink: 0;
  transition: transform 0.2s ease;
  ${props => props.$isOpen && `transform: rotate(180deg);`}
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  max-height: 280px;
  overflow-y: auto;
  margin-top: 4px;
`;

const DropdownOption = styled.div<{ $isSelected: boolean }>`
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: ${props => props.$isSelected ? '#ddf0ed' : '#ffffff'};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.$isSelected ? '#d4f0e0' : '#f8f9fa'};
  }
`;

export default MonetizedAvalesTab;
